<script lang="ts">
  import { onDestroy, untrack } from "svelte";
  import AdminShell from "@/components/AdminShell.svelte";
  import { api, ApiError, type AdminNode, type NodeAuditEvent, type NodeServer } from "@/api";
  import { token } from "@/stores/session";
  import { confirmModal, promptModal } from "@/stores/modal";
  import { route, routeHref } from "@/router";

  import { copyToClipboard } from "@/utils/clipboard";
  import { formatDateTime } from "@/utils/format";
  import { nodeStatusLabels, statusLabels } from "@/config";
  import AdminTable from "@/components/AdminTable.svelte";
  import EmptyState from "@/components/EmptyState.svelte";
  import FormField from "@/components/FormField.svelte";
  import FormGrid from "@/components/FormGrid.svelte";
  import LoadingState from "@/components/LoadingState.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import NumberInput from "@/components/NumberInput.svelte";
  import Button from "@/components/Button.svelte";

  function nodeStatusText(node: AdminNode): string {
    return nodeStatusLabels[node.status] ?? node.status;
  }

  let nodes: AdminNode[] = $state([]);
  let events: NodeAuditEvent[] = $state([]);
  let auditError = $state<string | null>(null);
  let selectedNode = $state<AdminNode | null>(null);
  let selectedServers: NodeServer[] = $state([]);
  let busy = $state(false);
  let loading = $state(true);
  let nodeLoading = $state(false);
  let error = $state<string | null>(null);
  let oneTimeToken = $state<string | null>(null);
  let search = $state("");
  let debouncedSearch = $state("");
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let copiedField = $state<string | null>(null);

  // Форма редактирования в карточке ноды
  let editForm = $state({
    name: "",
    hostname: "",
    publicIp: "",
    region: "",
    capacityContainers: 20,
    maxRunningMemoryGb: undefined as number | undefined,
  });

  const isCardView = $derived($route.name === "admin-node" && Boolean($route.args.id));
  const activeNodeId = $derived(isCardView ? $route.args.id : null);

  $effect(() => {
    const term = search;
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    searchTimer = setTimeout(() => {
      debouncedSearch = term;
    }, 300);
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  });

  let form = $state({
    name: "",
    hostname: "",
    publicIp: "",
    region: "",
    capacityContainers: 20,
    maxRunningMemoryGb: undefined as number | undefined,
  });
  const filteredNodes = $derived(nodes);
  let page = $state(1);
  let nextCursor = $state<string | null>(null);
  let cursors: Array<string | null> = [null];
  let failedTarget = 1;
  let failedServersTarget = 1;
  let serversPage = $state(1);
  let serversNextCursor = $state<string | null>(null);
  let serversCursors: Array<string | null> = [null];
  let serversLoading = $state(false);
  let serversError = $state<string | null>(null);
  let listController: AbortController | null = null;
  let detailController: AbortController | null = null;
  let serversController: AbortController | null = null;

  async function copyText(text: string, field: string) {
    if (!(await copyToClipboard(text))) {
      error = "Не удалось скопировать значение. Выделите его и скопируйте вручную.";
      return;
    }
    copiedField = field;
    setTimeout(() => {
      if (copiedField === field) {
        copiedField = null;
      }
    }, 1800);
  }

  function hideOneTimeToken() {
    oneTimeToken = null;
  }

  function handleSearchKeydown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
      debouncedSearch = search;
    }
  }

  function handleEditFormSubmit(event: SubmitEvent) {
    event.preventDefault();
    void saveNodeDetails();
  }

  function handleCreateNodeSubmit(event: SubmitEvent) {
    event.preventDefault();
    void createNode();
  }

  async function loadNodes(target = 1) {
    if (!$token) {
      return;
    }
    listController?.abort();
    const request = new AbortController();
    listController = request;
    const cursor = target === 1 ? null : target > page ? nextCursor : cursors[target - 1];
    failedTarget = target;
    loading = true;
    try {
      const result = await api.adminNodes(
        $token,
        { query: debouncedSearch, cursor },
        request.signal,
      );
      if (request.signal.aborted) {
        return;
      }
      nodes = result.items;
      nextCursor = result.nextCursor;
      cursors = target === 1 ? [null] : [...cursors.slice(0, target - 1), cursor];
      page = target;
      error = null;
      void loadAudit(request.signal);
    } catch (cause) {
      if (!request.signal.aborted) {
        error = cause instanceof Error ? cause.message : "Не удалось загрузить ноды";
      }
    } finally {
      if (!request.signal.aborted) {
        loading = false;
      }
    }
  }
  async function loadAudit(signal?: AbortSignal) {
    if (!$token) {
      return;
    }
    try {
      const result = await api.adminAudit($token, signal);
      if (!signal?.aborted) {
        events = result;
        auditError = null;
      }
    } catch (cause) {
      if (!signal?.aborted) {
        auditError = cause instanceof Error ? cause.message : "Не удалось загрузить журнал нод";
      }
    }
  }
  async function loadServers(id: string, target = 1) {
    if (!$token) {
      return;
    }
    serversController?.abort();
    const request = new AbortController();
    serversController = request;
    const cursor =
      target === 1 ? null : target > serversPage ? serversNextCursor : serversCursors[target - 1];
    failedServersTarget = target;
    serversLoading = true;
    serversError = null;
    try {
      const result = await api.nodeServers($token, id, { cursor }, request.signal);
      if (request.signal.aborted) {
        return;
      }
      selectedServers = result.items;
      serversNextCursor = result.nextCursor;
      serversCursors = target === 1 ? [null] : [...serversCursors.slice(0, target - 1), cursor];
      serversPage = target;
    } catch (cause) {
      if (!request.signal.aborted) {
        serversError = cause instanceof Error ? cause.message : "Не удалось загрузить серверы ноды";
      }
    } finally {
      if (!request.signal.aborted) {
        serversLoading = false;
      }
    }
  }
  async function loadNode(id: string) {
    if (!$token) {
      return;
    }
    detailController?.abort();
    const request = new AbortController();
    detailController = request;
    nodeLoading = true;
    selectedServers = [];
    serversPage = 1;
    serversNextCursor = null;
    try {
      const node = await api.adminNode($token, id, request.signal);
      if (request.signal.aborted) {
        return;
      }
      selectedNode = node;
      editForm = {
        name: node.name,
        hostname: node.hostname ?? "",
        publicIp: node.publicIp ?? "",
        region: node.region ?? "",
        capacityContainers: node.capacityContainers,
        maxRunningMemoryGb:
          node.maxRunningMemoryMb === null ? undefined : node.maxRunningMemoryMb / 1024,
      };
      error = null;
      void loadServers(id);
    } catch (cause) {
      if (!request.signal.aborted) {
        error = cause instanceof Error ? cause.message : "Не удалось загрузить карточку ноды";
      }
    } finally {
      if (!request.signal.aborted) {
        nodeLoading = false;
      }
    }
  }
  onDestroy(() => {
    listController?.abort();
    detailController?.abort();
    serversController?.abort();
  });

  async function saveNodeDetails() {
    if (!$token || !selectedNode || busy) {
      return;
    }
    if (!editForm.name.trim()) {
      error = "Имя ноды не может быть пустым";
      return;
    }
    const cap = Number(editForm.capacityContainers);
    if (isNaN(cap) || cap < 1 || cap > 200) {
      error = "Лимит серверов должен быть числом от 1 до 200";
      return;
    }
    busy = true;
    error = null;
    try {
      const updated = await api.updateNode($token, selectedNode.id, {
        name: editForm.name.trim(),
        hostname: editForm.hostname.trim(),
        publicIp: editForm.publicIp.trim(),
        region: editForm.region.trim(),
        capacityContainers: cap,
        maxRunningMemoryMb: toMemoryMb(editForm.maxRunningMemoryGb),
      });
      selectedNode = updated;
      await loadNodes();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось обновить данные ноды";
    } finally {
      busy = false;
    }
  }

  async function action(type: "rotate" | "revoke" | "drain" | "disable" | "enable") {
    if (!$token || !selectedNode || busy) {
      return;
    }
    if (
      type === "revoke" &&
      !(await confirmModal("Отозвать токен", "Агент потеряет доступ к панели.", "Отозвать"))
    ) {
      return;
    }
    if (
      type === "disable" &&
      !(await confirmModal(
        "Отключить ноду",
        "Нода перестанет принимать новые аллокации.",
        "Отключить",
      ))
    ) {
      return;
    }
    busy = true;
    try {
      if (type === "rotate") {
        oneTimeToken = (await api.rotateNodeToken($token, selectedNode.id)).token;
      } else if (type === "revoke") {
        await api.revokeNodeToken($token, selectedNode.id);
      } else {
        await api.setNodeStatus($token, selectedNode.id, type);
      }
      if (activeNodeId) {
        await loadNode(activeNodeId);
      }
      await loadNodes();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Операция над нодой не выполнена";
    } finally {
      busy = false;
    }
  }

  async function editCapacityQuick() {
    if (!$token || !selectedNode || busy) {
      return;
    }
    const raw = await promptModal(
      "Изменение лимита серверов",
      String(selectedNode.capacityContainers),
      "Лимит контейнеров (1–200)",
      "Сохранить",
    );
    if (raw === null) {
      return;
    }
    if (!/^\d+$/.test(raw.trim())) {
      error = "Введите целое число от 1 до 200";
      return;
    }
    const val = Number(raw.trim());
    if (val < 1 || val > 200) {
      error = "Лимит серверов должен быть в диапазоне от 1 до 200";
      return;
    }
    busy = true;
    error = null;
    try {
      await api.updateNode($token, selectedNode.id, { capacityContainers: val });
      if (activeNodeId) {
        await loadNode(activeNodeId);
      }
      await loadNodes();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Не удалось обновить лимит серверов";
    } finally {
      busy = false;
    }
  }

  function toMemoryMb(value: number | null | undefined): number | null {
    if (value === undefined || value === null) {
      return null;
    }
    const mb = value * 1024;
    if (!Number.isInteger(mb) || mb < 0 || mb > 100_000_000) {
      throw new Error("Укажите корректный лимит ОЗУ в ГБ (0 — приостановить запуски)");
    }
    return mb;
  }

  async function createNode() {
    if (!$token || !form.name.trim() || busy) {
      return;
    }
    busy = true;
    try {
      const { maxRunningMemoryGb, ...nodeForm } = form;
      const result = await api.createNode($token, {
        ...nodeForm,
        name: form.name.trim(),
        maxRunningMemoryMb: toMemoryMb(maxRunningMemoryGb),
      });
      oneTimeToken = result.token;
      form = {
        name: "",
        hostname: "",
        publicIp: "",
        region: "",
        capacityContainers: 20,
        maxRunningMemoryGb: undefined as number | undefined,
      };
      await loadNodes();
    } catch (cause) {
      error = cause instanceof ApiError ? cause.message : "Нода не создана";
    } finally {
      busy = false;
    }
  }

  $effect(() => {
    const id = activeNodeId;
    const query = debouncedSearch;
    const bearer = $token;
    untrack(() => {
      if (!bearer) {
        return;
      }
      if (id) {
        listController?.abort();
        void loadNode(id);
      } else {
        detailController?.abort();
        serversController?.abort();
        void loadNodes(1);
      }
    });
    void query;
  });
</script>

<AdminShell section="nodes">
  {#if error}
    <div class="form-error" role="alert">
      {error}
      {#if !isCardView}<Button disabled={loading} onclick={() => void loadNodes(failedTarget)}
          >Повторить загрузку</Button
        >{/if}
      {#if isCardView && !selectedNode}
        <Button
          variant="ghost"
          onclick={() => activeNodeId && void loadNode(activeNodeId)}
          disabled={nodeLoading}>Повторить загрузку</Button
        >
      {/if}
    </div>
  {/if}

  {#if oneTimeToken}
    <section class="admin-secret">
      <p class="eyebrow">Одноразовый токен агента</p>
      <code>{oneTimeToken}</code>
      <Button variant="ghost" onclick={hideOneTimeToken}>Скрыть</Button>
    </section>
  {/if}

  {#if isCardView}
    <!-- КАРТОЧКА НОДЫ (ОТДЕЛЬНАЯ СТРАНИЦА) -->
    <a class="back-link" href={routeHref("admin-nodes")}>← Все ноды</a>

    {#if nodeLoading || (!selectedNode && !error)}
      <LoadingState message="Загрузка карточки ноды…" />
    {:else if selectedNode}
      {@const node = selectedNode}
      <section class="panel-card admin-card detail-card">
        <header class="detail-heading">
          <div>
            <p class="eyebrow">Карточка ноды</p>
            <h2>{node.name}</h2>
            <div class="header-chips">
              <button
                class="meta-chip chip-mono"
                type="button"
                onclick={() => copyText(node.id, "id")}
                title="Нажмите, чтобы скопировать ID"
              >
                <span>ID: {node.id.slice(0, 8)}…</span>
                <small>{copiedField === "id" ? "скопировано" : "копировать"}</small>
              </button>
              {#if node.publicIp}
                <button
                  class="meta-chip chip-mono"
                  type="button"
                  onclick={() => copyText(node.publicIp, "ip")}
                  title="Нажмите, чтобы скопировать IP"
                >
                  <span>IP: {node.publicIp}</span>
                  <small>{copiedField === "ip" ? "скопировано" : "копировать"}</small>
                </button>
              {/if}
            </div>
          </div>
          <span class={`node-status ${node.status}`}>{nodeStatusText(node)}</span>
        </header>

        <!-- Статистика и факты -->
        <dl class="detail-facts">
          <div>
            <dt>Статус аллокаций</dt>
            <dd>{nodeStatusText(node)}</dd>
          </div>
          <div>
            <dt>Последний сигнал</dt>
            <dd>
              {node.heartbeatAgeSec === null ? "нет сигнала" : `${node.heartbeatAgeSec} сек назад`}
            </dd>
          </div>
          <div>
            <dt>Лимит размещения</dt>
            <dd>{node.capacityContainers} контейнеров</dd>
          </div>
          <div>
            <dt>Память серверов: выделено / лимит</dt>
            <dd>
              {(node.allocatedRunningMemoryMb / 1024).toLocaleString("ru-RU")} / {(
                Math.min(node.maxRunningMemoryMb ?? node.memTotalMb ?? 0, node.memTotalMb ?? 0) /
                1024
              ).toLocaleString("ru-RU")} ГБ
            </dd>
          </div>
          <div>
            <dt>Оперативная память</dt>
            <dd>{node.memTotalMb ? `${node.memTotalMb} МБ` : "нет данных"}</dd>
          </div>
          <div>
            <dt>Регион</dt>
            <dd>{node.region || "не указан"}</dd>
          </div>

          <div>
            <dt>Имя узла (hostname)</dt>
            <dd>{node.hostname || "не указан"}</dd>
          </div>
          <div>
            <dt>Серверов на странице</dt>
            <dd>{selectedServers.length}</dd>
          </div>
          <div>
            <dt>Зарегистрирована</dt>
            <dd>{formatDateTime(node.createdAt)}</dd>
          </div>
        </dl>

        <!-- Быстрые действия оператора -->
        <div class="admin-actions">
          <Button onclick={() => void editCapacityQuick()} disabled={busy}>
            Изменить лимит серверов
          </Button>
          <Button variant="ghost" onclick={() => void action("rotate")} disabled={busy}>
            Новый токен
          </Button>
          <Button variant="ghost" onclick={() => void action("revoke")} disabled={busy}>
            Отозвать токен
          </Button>
          {#if node.status === "online"}
            <Button variant="ghost" onclick={() => void action("drain")} disabled={busy}>
              Вывести из распределения
            </Button>
            <Button variant="danger" onclick={() => void action("disable")} disabled={busy}>
              Отключить
            </Button>
          {:else}
            <Button variant="ghost" onclick={() => void action("enable")} disabled={busy}>
              Включить
            </Button>
          {/if}
        </div>

        <!-- Форма редактирования параметров ноды -->
        <div class="card-edit-section">
          <h3>Редактирование параметров ноды</h3>
          <form class="node-edit-form" onsubmit={handleEditFormSubmit}>
            <FormGrid>
              <FormField label="Имя ноды"
                ><TextInput
                  bind:value={editForm.name}
                  required
                  maxlength={64}
                  disabled={busy}
                /></FormField
              >
              <FormField label="Имя хоста"
                ><TextInput
                  bind:value={editForm.hostname}
                  maxlength={255}
                  disabled={busy}
                /></FormField
              >
            </FormGrid>
            <FormGrid>
              <FormField label="Публичный IP"
                ><TextInput
                  bind:value={editForm.publicIp}
                  maxlength={45}
                  disabled={busy}
                /></FormField
              >
              <FormField label="Регион"
                ><TextInput
                  bind:value={editForm.region}
                  maxlength={64}
                  disabled={busy}
                /></FormField
              >
              <FormField label="Лимит контейнеров"
                ><NumberInput
                  bind:value={editForm.capacityContainers}
                  min={1}
                  max={200}
                  required
                  disabled={busy}
                /></FormField
              >
              <FormField
                label="ОЗУ для запуска серверов, ГБ"
                hint="Суммарная выделенная ОЗУ. Пусто — вся ОЗУ узла, 0 — ожидание в очереди. Лимит не превышает физическую память."
              >
                <NumberInput
                  bind:value={editForm.maxRunningMemoryGb}
                  min={0}
                  max={97656.25}
                  step="any"
                  placeholder="Вся ОЗУ узла"
                  disabled={busy}
                />
              </FormField>
            </FormGrid>
            <Button type="submit" disabled={busy || !editForm.name.trim()}>
              {busy ? "Сохраняем…" : "Сохранить изменения"}
            </Button>
          </form>
        </div>

        <!-- Серверы на ноде -->
        <div class="card-servers-section">
          <div class="card-servers-header">
            <h3>Серверы на этой ноде · на странице <small>({selectedServers.length})</small></h3>
          </div>
          {#if serversError}<p role="alert">
              {serversError}
              <Button
                disabled={serversLoading}
                onclick={() =>
                  selectedNode && void loadServers(selectedNode.id, failedServersTarget)}
                >Повторить серверы</Button
              >
            </p>{/if}
          {#if serversLoading}<p role="status">Загружаем серверы…</p>{/if}
          {#if selectedServers.length === 0 && !serversError && !serversLoading}
            <p class="muted">На странице нет размещённых серверов.</p>
          {:else}
            <AdminTable
              label="Таблица серверов на выбранной ноде. Прокрутите горизонтально для просмотра всех столбцов"
            >
              <thead>
                <tr>
                  <th>Сервер</th>
                  <th>Статус</th>
                  <th>Владелец</th>
                  <th>Обновлён</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {#each selectedServers as s (s.id)}
                  <tr>
                    <td>
                      <a class="table-link" href={routeHref("admin-server", { id: s.id })}>
                        <strong>{s.name}</strong>
                      </a>
                    </td>
                    <td>
                      <span class={`node-status ${s.status}`}
                        >{statusLabels[s.status] ?? s.status}</span
                      >
                    </td>
                    <td>
                      <a class="table-link" href={routeHref("admin-user", { id: s.ownerId })}>
                        {s.ownerUsername ?? "Владелец"}
                      </a>
                    </td>
                    <td>{formatDateTime(s.updatedAt)}</td>
                    <td class="table-actions">
                      <a
                        class="ghost-button compact"
                        href={routeHref("admin-server", { id: s.id })}
                      >
                        Открыть
                      </a>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </AdminTable>
          {/if}
          <div class="pagination">
            <Button
              disabled={serversLoading || serversPage === 1}
              onclick={() => selectedNode && void loadServers(selectedNode.id, serversPage - 1)}
              >Назад</Button
            ><span>Серверы · страница {serversPage}</span><Button
              disabled={serversLoading || !serversNextCursor}
              onclick={() => selectedNode && void loadServers(selectedNode.id, serversPage + 1)}
              >Далее</Button
            >
          </div>
        </div>
      </section>
    {/if}
  {:else}
    <!-- СПИСОК ВСЕХ НОД + ДОБАВЛЕНИЕ + АУДИТ -->
    <section class="panel-card admin-card admin-wide-card">
      <div class="admin-toolbar">
        <div>
          <p class="eyebrow">Ноды</p>
          <h2>Игровые ноды · на странице <small>{nodes.length}</small></h2>
        </div>
        <Button variant="ghost" onclick={() => void loadNodes(page)} disabled={loading}
          >Обновить</Button
        >
      </div>

      <TextInput
        density="compact"
        flexible
        bind:value={search}
        onkeydown={handleSearchKeydown}
        placeholder="Поиск ноды по имени, региону или IP/hostname"
        aria-label="Поиск нод"
      />

      {#if loading}
        <LoadingState message="Загрузка нод…" compact />
      {:else if filteredNodes.length === 0}
        <EmptyState title="Ноды не найдены" message="Попробуйте изменить запрос поиска." />
      {:else}
        <AdminTable label="Таблица нод. Прокрутите горизонтально для просмотра всех столбцов">
          <thead>
            <tr>
              <th>Нода</th>
              <th>Статус</th>
              <th>Регион</th>
              <th>Адрес</th>
              <th>Лимит контейнеров</th>
              <th>Последний сигнал</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {#each filteredNodes as node (node.id)}
              <tr>
                <td>
                  <a class="table-link" href={routeHref("admin-node", { id: node.id })}>
                    <strong>{node.name}</strong>
                  </a>
                </td>
                <td>
                  <span class={`node-status ${node.status}`}>{nodeStatusText(node)}</span>
                </td>
                <td>{node.region || "—"}</td>
                <td>{node.publicIp || node.hostname || "—"}</td>
                <td>{node.capacityContainers}</td>
                <td>{node.heartbeatAgeSec === null ? "нет" : `${node.heartbeatAgeSec} сек`}</td>
                <td class="table-actions">
                  <a class="button compact" href={routeHref("admin-node", { id: node.id })}>
                    Управление →
                  </a>
                </td>
              </tr>
            {/each}
          </tbody>
        </AdminTable>
      {/if}
    </section>

    <div class="pagination">
      <Button disabled={loading || page === 1} onclick={() => void loadNodes(page - 1)}
        >Назад</Button
      ><span>Страница {page}</span><Button
        disabled={loading || !nextCursor}
        onclick={() => void loadNodes(page + 1)}>Далее</Button
      >
    </div>
    <!-- Добавление новой ноды -->
    <section class="panel-card admin-card create-node">
      <div class="admin-toolbar">
        <div>
          <p class="eyebrow">Создание ноды</p>
          <h2>Добавить новую ноду</h2>
        </div>
      </div>
      <form onsubmit={handleCreateNodeSubmit}>
        <FormField label="Имя"
          ><TextInput bind:value={form.name} required maxlength={64} /></FormField
        >
        <FormField label="Имя хоста"
          ><TextInput bind:value={form.hostname} maxlength={255} /></FormField
        >
        <FormField label="Публичный IP"
          ><TextInput bind:value={form.publicIp} maxlength={45} /></FormField
        >
        <FormField label="Регион"><TextInput bind:value={form.region} maxlength={64} /></FormField>
        <FormField label="Лимит контейнеров"
          ><NumberInput bind:value={form.capacityContainers} min={1} max={200} /></FormField
        >
        <FormField
          label="ОЗУ для запуска серверов, ГБ"
          hint="Пусто — вся ОЗУ узла. Например, 16 ГБ позволяют запустить восемь серверов по 2 ГБ."
        >
          <NumberInput
            bind:value={form.maxRunningMemoryGb}
            min={0}
            max={97656.25}
            step="any"
            placeholder="Вся ОЗУ узла"
          />
        </FormField>
        <Button type="submit" disabled={busy || !form.name.trim()}>Создать ноду</Button>
      </form>
    </section>

    <!-- Журнал операций с нодами -->
    <section class="panel-card admin-card audit-card">
      <p class="eyebrow">Журнал действий</p>
      <h2>Операции с нодами</h2>
      {#if auditError}<p role="alert">
          {auditError}
          <Button onclick={() => void loadAudit()}>Повторить журнал</Button>
        </p>{/if}
      {#if events.length === 0}
        <p class="muted">Событий пока нет.</p>
      {:else}
        <div class="audit-list">
          {#each events as event (event.id)}
            <div>
              <span>
                <strong>{event.action}</strong>
                <small>{event.actorUsername ?? "система"}</small>
              </span>
              <time>{formatDateTime(event.createdAt)}</time>
            </div>
          {/each}
        </div>
      {/if}
    </section>
  {/if}
</AdminShell>

<style>
  .header-chips {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.4rem;
    flex-wrap: wrap;
  }

  .meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: var(--bg-card);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.2rem 0.65rem;
    font-size: 0.78rem;
    color: var(--text-muted);
    cursor: pointer;
  }

  .meta-chip:hover {
    color: var(--text-main);
    border-color: var(--text-muted);
  }

  .meta-chip small {
    opacity: 0.7;
  }

  .chip-mono {
    font-family: var(--font-mono, monospace);
  }

  .detail-facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
    padding: 1.25rem;
    background: var(--bg-subtle, rgba(255, 255, 255, 0.02));
    border: 1px solid var(--line);
    border-radius: var(--radius-sm, 6px);
  }

  .detail-facts dt {
    font-size: 0.75rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }

  .detail-facts dd {
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0;
  }

  .card-edit-section,
  .card-servers-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--line);
  }

  .card-servers-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .card-edit-section h3,
  .card-servers-section h3 {
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  .node-edit-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 720px;
  }
</style>
